import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import OpenInNew from "@mui/icons-material/OpenInNew";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import SEOKeywordChipsInput from "../../../../components/common/SEOKeywordChipsInput";
import { getSeoFallbackImageUrl } from "../../../../components/common/seoUtils";
import type { AxiosResponse } from "axios";
import type { VenueData } from "../../types/venues.types";
import apiService from "../../../../service/Api";
import type {
  SEOConfigsApiResponse,
} from "../../../configurations/seo-management/services/seoConfig";
import type {
  SiteListApiItem,
  SitesApiResponse,
} from "../../../configurations/site-listings/services/siteListingConfig";

type SEOField = "title" | "description" | "keywords" | "focus_keyword" | "og_image_url";
type SEOMetadata = Record<SEOField, string>;

type VenuePublicationSEOData = {
  slug: string;
  public_url: string | null;
  website_url: string | null;
  cover_photo_id: number | null;
  can_publish: boolean;
  seo_active: boolean;
  seo_metadata: Partial<SEOMetadata>;
};

const EMPTY_METADATA: SEOMetadata = {
  title: "",
  description: "",
  keywords: "",
  focus_keyword: "",
  og_image_url: "",
};

const AUTO_FILL: Record<SEOField, string[]> = {
  title: [
    "{{venue_name}} | {{venue_type}} in {{city}}",
    "Book {{venue_name}} in {{city}}",
    "{{venue_name}} - Event Venue in {{city}}",
  ],
  description: [
    "Discover {{venue_name}}, a {{venue_type}} in {{city}}. Explore capacity, amenities and layouts.",
    "Find your next event venue in {{city}}. {{venue_name}} offers space for {{capacity}} guests.",
    "Explore {{venue_name}} in {{suburb}}, {{city}}, with {{amenities}}.",
  ],
  keywords: [
    "{{venue_name}}, {{venue_type}}, {{city}} venue",
    "event venue in {{city}}, {{venue_name}}, {{country}}",
    "{{venue_type}} hire, {{city}} events, {{venue_name}}",
  ],
  focus_keyword: [
    "{{venue_type}} in {{city}}",
    "{{venue_name}}",
    "event venue {{city}}",
  ],
  og_image_url: ["{{cover_image}}"],
};

function normalizeHost(value: string): string {
  try {
    return new URL(value.includes("://") ? value : `https://${value}`)
      .hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

async function loadSites(): Promise<SiteListApiItem[]> {
  const sites: SiteListApiItem[] = [];
  let next: string | null = "/api/configurations/sites/?page_size=1000";
  while (next) {
    const response: AxiosResponse<SitesApiResponse> = await apiService.get<SitesApiResponse>(next);
    sites.push(...response.data.results);
    const nextURL: URL | null = response.data.next
      ? new URL(response.data.next, "https://api.local")
      : null;
    next = nextURL ? `${nextURL.pathname}${nextURL.search}` : null;
  }
  return sites;
}

function interpolate(template: string, context: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_token, key: string) => context[key] ?? "");
}

function hostFromURL(value: string | null): string {
  return value ? normalizeHost(value) : "";
}

function metadataValue(metadata: Partial<SEOMetadata>, key: SEOField, fallback: string): string {
  return Object.prototype.hasOwnProperty.call(metadata, key) ? metadata[key] ?? "" : fallback;
}

export default function VenueSEO({
  venueData,
  active,
}: {
  venueData: VenueData;
  active: boolean;
}) {
  const theme = useTheme();
  const endpoint = `/api/venues/venue/${venueData.id}/publication/`;
  const [publication, setPublication] = useState<VenuePublicationSEOData | null>(null);
  const [draft, setDraft] = useState<SEOMetadata>(EMPTY_METADATA);
  const [baseValues, setBaseValues] = useState<SEOMetadata>(EMPTY_METADATA);
  const [seoActive, setSeoActive] = useState(false);
  const [savedSEOActive, setSavedSEOActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setWarning("");

    const load = async () => {
      try {
        const { data } = await apiService.get<VenuePublicationSEOData>(endpoint);
        if (cancelled) return;
        setPublication(data);
        const metadata = data.seo_active ? (data.seo_metadata || {}) : {};
        setSeoActive(Boolean(data.seo_active));
        setSavedSEOActive(Boolean(data.seo_active));

        const host = hostFromURL(data.website_url) || hostFromURL(data.public_url);
        if (!host) {
          setWarning("Venue website URL is not configured.");
          const initial = { ...EMPTY_METADATA, ...metadata };
          setBaseValues(initial);
          setDraft(initial);
          return;
        }

        const sites = await loadSites();
        if (cancelled) return;
        const site = sites.find((item) => normalizeHost(item.domain) === host);
        if (!site) {
          setWarning("No website configuration matches this venue website.");
          const initial: SEOMetadata = {
            title: metadataValue(metadata, "title", "{{venue_name}} | {{venue_type}} in {{city}}"),
            description: metadataValue(metadata, "description", "Discover {{venue_name}}, a {{venue_type}} in {{city}}."),
            keywords: metadataValue(metadata, "keywords", "{{venue_name}}, {{venue_type}}, {{city}} venue"),
            focus_keyword: metadataValue(metadata, "focus_keyword", "{{venue_type}} in {{city}}"),
            og_image_url: metadataValue(metadata, "og_image_url", "{{cover_image}}"),
          };
          setBaseValues(initial);
          setDraft(initial);
          return;
        }

        const response = await apiService.get<SEOConfigsApiResponse>(
          "/api/configurations/seo-configs/",
          { site: site.id, page_name: "venue_page", is_active: true, page_size: 1000 },
        );
        const config = response.data.results[0] || null;
        if (!cancelled) {
          const initial: SEOMetadata = {
            title: metadataValue(metadata, "title", config?.meta_title_template || "{{venue_name}} | {{venue_type}} in {{city}}"),
            description: metadataValue(metadata, "description", config?.meta_description_template || "Discover {{venue_name}}, a {{venue_type}} in {{city}}."),
            keywords: metadataValue(metadata, "keywords", config?.meta_keywords || "{{venue_name}}, {{venue_type}}, {{city}} venue"),
            focus_keyword: metadataValue(metadata, "focus_keyword", config?.focus_keyword || "{{venue_type}} in {{city}}"),
            og_image_url: metadataValue(metadata, "og_image_url", config?.og_image_url || "{{cover_image}}"),
          };
          setBaseValues(initial);
          setDraft(initial);
          if (!config) setWarning("No active venue-page SEO defaults are configured for this website.");
        }
      } catch {
        if (!cancelled) setError("Could not load venue SEO settings. Retry to try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [active, endpoint, attempt]);

  const publicPhotos = venueData.files?.filter((file) =>
    ["venue_photos", "room_photos"].includes(file.file_type)
    && /\.(jpe?g|png|webp|gif|avif)(?:$|\?)/i.test(file.file),
  ) || [];
  const coverPhoto = publicPhotos.find((file) => file.is_cover)
    || publicPhotos[0];

  const context = useMemo<Record<string, string>>(() => ({
    venue_name: venueData.name || "",
    name: venueData.name || "",
    venue_type: venueData.venue_type || "",
    city: venueData.city || "",
    suburb: venueData.suburb || "",
    address: [venueData.address_one, venueData.address_two].filter(Boolean).join(", "),
    country: venueData.country || "",
    capacity: String(venueData.capacity || ""),
    description: venueData.description || "",
    amenities: (venueData.amenities || []).join(", "),
    layouts: (venueData.layouts || []).map((layout) => `${layout.layout_type} (${layout.capacity})`).join(", "),
    cover_image: coverPhoto?.file || "",
    contact_phone: venueData.contact_phone || "",
    contact_email: venueData.contact_email || "",
  }), [venueData, coverPhoto]);

  const values: SEOMetadata = {
    ...draft,
  };
  const pageURL = publication?.public_url
    || (publication?.website_url && publication.slug
      ? `${publication.website_url.replace(/\/$/, "")}/venues/${publication.slug}`
      : `/venues/${publication?.slug || ""}`);
  const titlePreview = interpolate(values.title, context);
  const descriptionPreview = interpolate(values.description, context);
  const imagePreview = interpolate(values.og_image_url, context).trim() || getSeoFallbackImageUrl();
  const changed = seoActive !== savedSEOActive
    || (Object.keys(values) as SEOField[]).some((key) => draft[key] !== baseValues[key]);

  const update = (key: SEOField, value: string) => setDraft((current) => ({ ...current, [key]: value }));
  const updateSEOActive = (active: boolean) => {
    if (active && !seoActive && publication) {
      setDraft((current) => ({ ...current, ...publication.seo_metadata }));
    }
    setSeoActive(active);
  };
  const autoFill = (key: SEOField) => {
    if (key === "og_image_url" && !context.cover_image) {
      update(key, getSeoFallbackImageUrl());
      setSeoActive(true);
      return;
    }
    const choices = AUTO_FILL[key];
    update(key, choices[Math.floor(Math.random() * choices.length)]);
    setSeoActive(true);
  };

  const save = async () => {
    if (!publication?.can_publish) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await apiService.put<VenuePublicationSEOData>(endpoint, {
        action: "save",
        seo_active: seoActive,
        seo_metadata: {
          ...draft,
          og_image_url: interpolate(draft.og_image_url, context).trim()
            ? draft.og_image_url
            : getSeoFallbackImageUrl(),
        },
      });
      setPublication(data);
      const metadata = { ...EMPTY_METADATA, ...draft };
      setDraft(metadata);
      setBaseValues(metadata);
      setSavedSEOActive(Boolean(data.seo_active));
      setSeoActive(Boolean(data.seo_active));
    } catch {
      setError("Could not save venue SEO settings. Please retry.");
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;
  if (loading) return <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /><Typography>Loading venue SEO defaults…</Typography></Stack>;
  if (error && !publication) return <Alert severity="warning" action={<Button color="inherit" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>{error}</Alert>;
  if (!publication) return null;

  const fields: { key: SEOField; label: string; multiline?: boolean }[] = [
    { key: "title", label: "Page title template" },
    { key: "description", label: "Meta description template", multiline: true },
    { key: "og_image_url", label: "Open Graph image URL" },
    { key: "keywords", label: "Meta keywords template" },
    { key: "focus_keyword", label: "Focused keyword template" },
  ];

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button variant="contained" disabled={!changed || saving || !publication.can_publish} onClick={() => void save()} sx={{ textTransform: "uppercase" }}>
          {saving ? "Saving…" : "Save SEO"}
        </Button>
      </Stack>
      {!publication.can_publish && <Alert severity="info" sx={{ mb: 2 }}>You can view these SEO settings, but need venue publishing permission to change them.</Alert>}
      {warning && <Alert severity="info" sx={{ mb: 2 }}>{warning}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.7fr) minmax(280px, 1fr)" }, gap: 2, alignItems: "start" }}>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TuneOutlinedIcon color="action" />
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Venue SEO</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Manage how this venue appears in search results and social previews.</Typography>
              </Box>
            </Stack>
          </Box>
          <Divider />
          <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 3 } }}>
            {fields.map(({ key, label, multiline }) => (
              <Box key={key} sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) 132px" }, columnGap: 2, alignItems: "start" }}>
                <Box>
                  {key === "keywords" || key === "focus_keyword" ? (
                    <SEOKeywordChipsInput label={label} value={values[key]} onChange={(value) => update(key, value)} helperText={`Preview: ${interpolate(values[key], context)}`} />
                  ) : (
                    <TextField fullWidth multiline={multiline} minRows={multiline ? 4 : undefined} label={label} value={values[key]} onChange={(event) => update(key, event.target.value)} helperText={key === "title" ? `${titlePreview.length} of 70 recommended characters in preview` : key === "description" ? `${descriptionPreview.length} of 160 recommended characters in preview` : "Paste a public image URL or insert the venue cover image."} />
                  )}
                  {Object.entries(context).map(([variable, sample]) => (key === "og_image_url" && variable !== "cover_image") ? null : (
                    <Button key={variable} size="small" title={sample} onClick={() => update(key, `${values[key]}{{${variable}}}`)} sx={{ textTransform: "none", minWidth: 0, mr: 0.5 }}>{variable.replace(/_/g, " ")}</Button>
                  ))}
                </Box>
                <Button variant="outlined" startIcon={<AutoFixHighIcon />} disabled={saving || !publication.can_publish} onClick={() => autoFill(key)} sx={{ width: "100%", minWidth: 112, textTransform: "none", borderRadius: 2 }}>Auto-fill</Button>
              </Box>
            ))}
            <FormControlLabel control={<Checkbox checked={seoActive} disabled={!publication.can_publish} onChange={(event) => updateSEOActive(event.target.checked)} />} label="Use this SEO customization for this venue" />
            <Typography variant="body2" color="text.secondary">{seoActive ? "Active customization. The venue-specific SEO values will be used." : "Customization is inactive; website defaults remain in use."}</Typography>
          </Stack>
        </Paper>
        <Paper component="aside" variant="outlined" aria-label="SEO preview" sx={{ p: 2.5, borderRadius: 2, alignSelf: "start" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <OpenInNew fontSize="small" color="action" />
            <Typography fontWeight={800}>Search previews</Typography>
          </Stack>
          <Stack spacing={2.5} aria-live="polite" sx={{ overflowWrap: "anywhere" }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Google Search</Typography>
              <Typography sx={{ color: theme.palette.mode === "dark" ? "#8ab4f8" : "#1a0dab", fontSize: 18, lineHeight: 1.3, mt: 0.5 }}>{titlePreview || "Untitled page"}</Typography>
              <Typography variant="body2" sx={{ color: "#006621" }}>{pageURL}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{(descriptionPreview || "No description set.").slice(0, 160)}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">Facebook / Open Graph</Typography>
              {imagePreview && <Box component="img" src={imagePreview} alt="Open Graph preview" sx={{ display: "block", width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1, mt: 0.5, mb: 0.5 }} onError={(event) => { (event.target as HTMLImageElement).style.display = "none"; }} />}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{pageURL}</Typography>
              <Typography fontWeight={700} variant="body2">{titlePreview || "Untitled page"}</Typography>
              <Typography variant="body2" color="text.secondary">{descriptionPreview || "No description set."}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">Twitter / X</Typography>
              {imagePreview && <Box component="img" src={imagePreview} alt="Twitter preview" sx={{ display: "block", width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 1, mt: 0.5, mb: 0.5 }} onError={(event) => { (event.target as HTMLImageElement).style.display = "none"; }} />}
              <Typography fontWeight={700} variant="body2" sx={{ mt: 0.5 }}>{titlePreview || "Untitled page"}</Typography>
              <Typography variant="body2" color="text.secondary">{descriptionPreview || "No description set."}</Typography>
              <Typography variant="caption" color="text.secondary">{pageURL}</Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
